import React from 'react';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import { SettingsToggle } from '../components/SettingsToggle';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../utils/settingsSectionStyles';
import { EINVOICE_MANDATE_OPTIONS } from '../../invoices/utils/einvoiceMandate';
import { formatGermanNumber, parseGermanNumberOnBlur } from '../../../utils/parseGermanNumber';

type ComplianceValues = {
  incoming_approval_four_eyes: boolean;
  /** Betragsgrenze als Eingabetext (deutsches Format), leer = aus. */
  threshold: string;
  incoming_review_stale_days: string;
  einvoice_mandatory_from: string;
};

const EMPTY: ComplianceValues = {
  incoming_approval_four_eyes: false,
  threshold: '',
  incoming_review_stale_days: '10',
  einvoice_mandatory_from: '',
};

function useCompanySettingsCard<T>(fromRow: (row: Record<string, unknown>) => T) {
  const [values, setValues] = React.useState<T | null>(null);
  const [snapshot, setSnapshot] = React.useState<T | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/settings/company', { credentials: 'include' })
      .then(async (res) => {
        const body = (await res.json().catch(() => ({}))) as Record<string, unknown> & { error?: string };
        if (!res.ok) throw new Error(body.error || 'Einstellungen konnten nicht geladen werden.');
        return body;
      })
      .then((row) => {
        if (!cancelled) setValues(fromRow(row));
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Einstellungen konnten nicht geladen werden.');
      });
    return () => {
      cancelled = true;
    };
    // fromRow ist eine stabile Modulfunktion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (body: Record<string, unknown>) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/settings/company', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = (await res.json().catch(() => ({}))) as Record<string, unknown> & { error?: string };
      if (!res.ok) throw new Error(payload.error || 'Speichern fehlgeschlagen.');
      setValues(fromRow(payload));
      setSnapshot(null);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return { values, setValues, snapshot, setSnapshot, saving, saved, setSaved, error, setError, save };
}

function complianceFromRow(row: Record<string, unknown>): ComplianceValues {
  const threshold = Number(row.incoming_approval_second_threshold);
  return {
    incoming_approval_four_eyes: row.incoming_approval_four_eyes === true,
    threshold: row.incoming_approval_second_threshold != null && Number.isFinite(threshold) && threshold > 0
      ? formatGermanNumber(threshold)
      : '',
    incoming_review_stale_days: String(row.incoming_review_stale_days ?? EMPTY.incoming_review_stale_days),
    einvoice_mandatory_from: typeof row.einvoice_mandatory_from === 'string' ? row.einvoice_mandatory_from.slice(0, 10) : '',
  };
}

function CardActions({
  editing,
  saving,
  saved,
  dirty,
  loaded,
  onEdit,
  onCancel,
  onSave,
}: {
  editing: boolean;
  saving: boolean;
  saved: boolean;
  dirty: boolean;
  loaded: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  return (
    <>
      <SettingsCardSavedStatus saved={saved && !editing} />
      {editing ? (
        <>
          <button type="button" className={SETTINGS_BTN_OUTLINE} disabled={saving} onClick={onCancel}>
            Abbrechen
          </button>
          <button type="button" className={SETTINGS_BTN_PRIMARY} disabled={saving || !dirty} onClick={onSave}>
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
        </>
      ) : (
        <button type="button" className={SETTINGS_BTN_OUTLINE} disabled={!loaded} onClick={onEdit}>
          Bearbeiten
        </button>
      )}
    </>
  );
}

/** Eine Einstellung: Titel und Erklärung links, Wert bzw. Bedienelement rechts. */
function SettingRow({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="min-w-0">
        <p className={SETTINGS_FIELD_LABEL}>{title}</p>
        <p className="mt-1 text-sm text-app-text-muted">{description}</p>
      </div>
      <div className="flex shrink-0 items-center gap-3 sm:justify-end">{children}</div>
    </div>
  );
}

/** Freigabe von Eingangsrechnungen: Vier-Augen-Prinzip, zweite Freigabe ab Betrag, Hinweis bei langer Prüfung. */
export function IncomingApprovalSettingsSection() {
  const card = useCompanySettingsCard(complianceFromRow);
  const { values, snapshot } = card;
  const editing = snapshot != null;
  const dirty = Boolean(values && snapshot && JSON.stringify(values) !== JSON.stringify(snapshot));
  // Zweite Freigabe als eigener Schalter; der Betrag ist nur bei „an“ relevant.
  const [secondOn, setSecondOn] = React.useState(false);
  React.useEffect(() => {
    if (values && !editing) setSecondOn(values.threshold.trim() !== '');
  }, [values, editing]);

  const save = () => {
    if (!values) return;
    const thresholdText = secondOn ? values.threshold.trim() : '';
    const threshold = thresholdText ? parseGermanNumberOnBlur(thresholdText) : null;
    if (secondOn && (threshold == null || threshold <= 0)) {
      card.setError('Bitte den Betrag eintragen, ab dem zwei Freigaben nötig sind.');
      return;
    }
    const staleDays = Math.floor(Number(values.incoming_review_stale_days));
    if (!Number.isFinite(staleDays) || staleDays < 1 || staleDays > 365) {
      card.setError('Bitte zwischen 1 und 365 Tagen eintragen.');
      return;
    }
    void card.save({
      incoming_approval_four_eyes: values.incoming_approval_four_eyes,
      incoming_approval_second_threshold: threshold,
      incoming_review_stale_days: staleDays,
    });
  };

  const thresholdDisplay = (() => {
    const n = values?.threshold.trim() ? parseGermanNumberOnBlur(values.threshold.trim()) : null;
    return n != null && n > 0 ? `ab ${formatGermanNumber(n)} €` : 'Aus';
  })();

  return (
    <SettingsCard
      title="Freigabe von Eingangsrechnungen"
      description="Neue Eingangsrechnungen bleiben in „Prüfung“, bis jemand mit dem Recht „Eingangsrechnungen freigeben“ sie freigibt. Erst dann sind sie gebucht und können bezahlt werden."
      actions={
        <CardActions
          editing={editing}
          saving={card.saving}
          saved={card.saved}
          dirty={dirty || (values != null && secondOn !== (values.threshold.trim() !== ''))}
          loaded={values != null}
          onEdit={() => {
            card.setSnapshot(values);
            card.setSaved(false);
            card.setError(null);
          }}
          onCancel={() => {
            card.setValues(snapshot);
            card.setSnapshot(null);
            card.setError(null);
            if (snapshot) setSecondOn(snapshot.threshold.trim() !== '');
          }}
          onSave={save}
        />
      }
    >
      {card.error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{card.error}</div>
      ) : null}
      {!values && !card.error ? <InlineLoadingState label="Einstellungen werden geladen …" /> : null}
      {values ? (
        <div className="divide-y divide-app-border">
          <SettingRow
            title="Vier-Augen-Prinzip"
            description="Wer einen Beleg hochgeladen hat, darf ihn nicht selbst freigeben. Belege aus dem Rechnungspostfach kann jede berechtigte Person freigeben."
          >
            <SettingsToggle
              active={values.incoming_approval_four_eyes}
              disabled={!editing || card.saving}
              label="Vier-Augen-Prinzip"
              onClick={() => card.setValues({ ...values, incoming_approval_four_eyes: !values.incoming_approval_four_eyes })}
            />
          </SettingRow>

          <SettingRow
            title="Zweite Freigabe bei hohen Beträgen"
            description="Ab einem Bruttobetrag müssen zwei verschiedene Personen freigeben."
          >
            {editing ? (
              <>
                {secondOn ? (
                  <div className="relative w-36">
                    <input
                      inputMode="decimal"
                      value={values.threshold}
                      disabled={card.saving}
                      placeholder="5.000"
                      aria-label="Betrag, ab dem zwei Freigaben nötig sind"
                      onChange={(e) => card.setValues({ ...values, threshold: e.target.value })}
                      className={`${SETTINGS_INPUT_CLASS} pr-8 text-right tabular-nums`}
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-app-text-subtle">€</span>
                  </div>
                ) : null}
                <SettingsToggle
                  active={secondOn}
                  disabled={card.saving}
                  label="Zweite Freigabe bei hohen Beträgen"
                  onClick={() => setSecondOn((on) => !on)}
                />
              </>
            ) : (
              <span className="text-sm text-app-text tabular-nums">{thresholdDisplay}</span>
            )}
          </SettingRow>

          <SettingRow
            title="Hinweis bei langer Prüfung"
            description="Belege, die länger in Prüfung liegen, werden in der Rechnungsliste markiert (zeitnahe Erfassung nach GoBD)."
          >
            {editing ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-app-text-muted">nach</span>
                <input
                  inputMode="numeric"
                  value={values.incoming_review_stale_days}
                  disabled={card.saving}
                  aria-label="Tage in Prüfung bis zum Hinweis"
                  onChange={(e) => card.setValues({ ...values, incoming_review_stale_days: e.target.value.replace(/[^\d]/g, '') })}
                  className={`${SETTINGS_INPUT_CLASS} w-16 text-right tabular-nums`}
                />
                <span className="text-sm text-app-text-muted">Tagen</span>
              </div>
            ) : (
              <span className="text-sm text-app-text tabular-nums">nach {values.incoming_review_stale_days} Tagen</span>
            )}
          </SettingRow>
        </div>
      ) : null}
    </SettingsCard>
  );
}

/**
 * Stichtag der E-Rechnungspflicht für Ausgangsrechnungen an Unternehmen.
 * Derzeit nicht eingeblendet (OffersInvoicesSettingsSection); ohne Stichtag greift die Sperre nicht.
 */
export function EinvoiceMandateSettingsSection() {
  const card = useCompanySettingsCard(complianceFromRow);
  const { values, snapshot } = card;
  const editing = snapshot != null;
  const dirty = Boolean(values && snapshot && values.einvoice_mandatory_from !== snapshot.einvoice_mandatory_from);
  const options = EINVOICE_MANDATE_OPTIONS.some((option) => option.value === values?.einvoice_mandatory_from)
    ? EINVOICE_MANDATE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))
    : [
        ...EINVOICE_MANDATE_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
        { value: values?.einvoice_mandatory_from ?? '', label: `Ab ${String(values?.einvoice_mandatory_from ?? '').split('-').reverse().join('.')}` },
      ];

  return (
    <SettingsCard
      title="E-Rechnungspflicht"
      description="Ab dem Stichtag werden Rechnungen an Unternehmen nur noch mit gültiger E-Rechnung (ZUGFeRD) ausgestellt. Fehlen Angaben für das XML, wird kein reines PDF erzeugt. Ausgenommen: Privatkunden, Kleinbetragsrechnungen bis 250 € und Empfänger im Ausland."
      actions={
        <CardActions
          editing={editing}
          saving={card.saving}
          saved={card.saved}
          dirty={dirty}
          loaded={values != null}
          onEdit={() => {
            card.setSnapshot(values);
            card.setSaved(false);
            card.setError(null);
          }}
          onCancel={() => {
            card.setValues(snapshot);
            card.setSnapshot(null);
            card.setError(null);
          }}
          onSave={() => {
            if (values) void card.save({ einvoice_mandatory_from: values.einvoice_mandatory_from || null });
          }}
        />
      }
    >
      {card.error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{card.error}</div>
      ) : null}
      {!values && !card.error ? <InlineLoadingState label="Einstellungen werden geladen …" /> : null}
      {values ? (
        <label className="block max-w-md">
          <span className={SETTINGS_FIELD_LABEL}>Pflicht gilt</span>
          <div className="mt-1">
            <AppSelectDropdown
              value={values.einvoice_mandatory_from}
              onChange={(value) => card.setValues({ ...values, einvoice_mandatory_from: value })}
              options={options}
              disabled={!editing || card.saving}
              triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
            />
          </div>
          <span className="mt-1 block text-xs text-app-text-subtle">
            Maßgeblich ist der Umsatz des Vorjahres. Im Zweifel mit der Steuerberatung abstimmen.
          </span>
        </label>
      ) : null}
    </SettingsCard>
  );
}
